import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useLegal } from '../lib/legalStrings';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { 
  Sparkles, ArrowLeft, User, Moon, Globe, Shield, Mail, Trash2, AlertTriangle, Download, Loader2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import DeleteAccountDialog from '../components/DeleteAccountDialog';

export default function SettingsPage() {
  const { t, language, setLanguage, languages } = useLanguage();
  const L = useLegal(language);
  const { theme, toggleTheme } = useTheme();
  const { user, logout, refreshUser } = useAuth();
  
  const [marketingConsent, setMarketingConsent] = useState(!!user?.marketing_consent);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleDataExport = async () => {
    setExportLoading(true);
    try {
      const res = await api.post('/auth/data-export');
      // Use the localized message the backend returns for BG, English fallback otherwise
      const msg = language === 'bg'
        ? res.data.message
        : (res.data.message_en || res.data.message);
      toast.success(msg, { duration: 6000 });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 429) {
        toast.error(detail || 'A recent export is still ready — check your inbox.');
      } else {
        toast.error(detail || 'Failed to request data export');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleToggleMarketing = async (checked) => {
    setMarketingConsent(checked);
    setSavingPrefs(true);
    try {
      await api.patch('/auth/preferences', { marketing_consent: checked });
      toast.success(L.settings.preferencesUpdated);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
      setMarketingConsent(!checked);
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <div className="noise-overlay" />
      
      <DeleteAccountDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-6 py-4 max-w-3xl mx-auto">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-heading text-xl font-bold">{t('dashboard.settings')}</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        {/* Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold">Profile</h2>
              <p className="text-sm text-muted-foreground">Manage your account information</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
              ) : (
                <span className="text-2xl font-bold text-primary">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              )}
            </div>
            <div>
              <p className="font-semibold">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </motion.div>

        {/* Appearance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="dashboard-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Moon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold">Appearance</h2>
              <p className="text-sm text-muted-foreground">Customize your experience</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-muted-foreground">Toggle dark theme</p>
            </div>
            <Switch 
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              data-testid="dark-mode-toggle"
            />
          </div>
        </motion.div>

        {/* Language */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="dashboard-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold">Language</h2>
              <p className="text-sm text-muted-foreground">Choose your preferred language</p>
            </div>
          </div>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-full" data-testid="language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  <div className="flex items-center gap-2">
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Email preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="dashboard-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold">{L.settings.preferencesTitle}</h2>
              <p className="text-sm text-muted-foreground">Manage your email opt-ins</p>
            </div>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium text-sm">{L.settings.marketingConsentLabel}</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {L.settings.marketingConsentHelp}
              </p>
            </div>
            <Switch
              checked={marketingConsent}
              onCheckedChange={handleToggleMarketing}
              disabled={savingPrefs}
              data-testid="marketing-consent-toggle"
            />
          </div>
        </motion.div>

        {/* Account (logout) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="dashboard-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
              <Shield className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <h2 className="font-heading font-semibold">Account</h2>
              <p className="text-sm text-muted-foreground">Sign out of your account</p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            Logout
          </Button>
        </motion.div>

        {/* GDPR Article 20 — Data Export */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          className="dashboard-card"
          data-testid="data-export-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Download className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold">{L.settings.dataExportTitle}</h2>
              <p className="text-sm text-muted-foreground">GDPR — right to data portability</p>
            </div>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium text-sm">{L.settings.dataExportLabel}</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {L.settings.dataExportHelp}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleDataExport}
              disabled={exportLoading}
              data-testid="data-export-btn"
              className="flex-shrink-0"
            >
              {exportLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {L.settings.dataExportBtn}
            </Button>
          </div>
        </motion.div>

        {/* Danger zone — delete account */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="dashboard-card border-destructive/40"
          data-testid="danger-zone"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-destructive">
                {L.settings.dangerTitle}
              </h2>
              <p className="text-sm text-muted-foreground">GDPR — right to erasure</p>
            </div>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium text-sm">{L.settings.deleteAccountLabel}</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {L.settings.deleteAccountHelp}
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-account-btn"
              className="flex-shrink-0"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {L.settings.deleteAccountBtn}
            </Button>
          </div>
        </motion.div>

        {/* Legal links footer */}
        <div className="text-center text-xs text-muted-foreground pt-4">
          <Link to="/privacy" className="hover:text-primary underline underline-offset-2 mr-4">
            {L.common.privacy}
          </Link>
          <Link to="/terms" className="hover:text-primary underline underline-offset-2">
            {L.common.terms}
          </Link>
        </div>
      </main>
    </div>
  );
}
